import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VITAL_SIGNS, type VitalKey } from '@wodi/domain';
import type { ClinicalAlertRule, NursingView } from '@wodi/shared';
import { AlertPanel, Button, Kbd, Notice, RadioGroup, useShortcut, VitalField } from '@wodi/ui';
import { useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { recordVitalsRound } from '../api/nursing.ts';
import { clinicalRulesQuery } from '../api/triage.ts';
import { useOnline } from '../hooks/use-online.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import {
  ALERT_FALLBACK,
  bloodPressureError,
  domainRules,
  FORM_VITALS,
  judgeField,
  criticalVitalsAlert,
  vitalInputs,
  type VitalTexts,
} from '../triage/triage-model.ts';
import { toasts } from '../toasts.ts';
import styles from './nursing.module.css';

// A vitals round on the ward (docs/plans/phase2-ward-rounds-nursing.md §1.1 item 4): the
// triage vitals fields, in the paper-chart order, judged as they are typed by the same
// rules the server applies (triage-model.ts). The server is the authority: an impossible
// value is refused, an out-of-range one flagged, and a critical one needs a reason, logged
// with its rule ID. Only the critical alert applies here; the triage-only alerts don't.

const ROUND_VITALS = FORM_VITALS.filter((k) => k !== 'muac');

export function VitalsRoundForm({ view }: { view: NursingView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const rulesView = useQuery(clinicalRulesQuery);
  const [texts, setTexts] = useState<VitalTexts>({});
  const [glucoseContext, setGlucoseContext] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Partial<Record<ClinicalAlertRule, string>>>({});
  const [serverAlerts, setServerAlerts] = useState<ClinicalAlertRule[]>([]);
  const [tried, setTried] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const rules = useMemo(() => domainRules(rulesView.data?.rules ?? []), [rulesView.data]);
  const ageDays = view.patientAgeDays;

  const critical = criticalVitalsAlert({ texts, glucoseContext, rules, ageDays });
  const foreseen = critical === null ? [] : [critical];
  const alerts = [
    ...foreseen,
    ...serverAlerts
      .filter((id) => !foreseen.some((a) => a.ruleId === id))
      .map((ruleId) => ({ ruleId, ...ALERT_FALLBACK[ruleId] })),
  ];
  const reasonOf = (id: ClinicalAlertRule) => reasons[id]?.trim() ?? '';
  const bpError = bloodPressureError(texts);
  const fieldError = ROUND_VITALS.some(
    (key) =>
      judgeField(
        key,
        texts[key] ?? '',
        rules,
        ageDays,
        key === 'blood_glucose' ? glucoseContext : null,
      ).error !== undefined,
  );
  const inputs = vitalInputs(texts, glucoseContext);

  const save = useMutation({
    mutationFn: () =>
      recordVitalsRound(view.admission.id, {
        clientRequestId: requestId.current,
        vitals: inputs,
        acknowledgements: alerts.map((a) => ({ ruleId: a.ruleId, reason: reasonOf(a.ruleId) })),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'nursing', view.admission.id], next);
      void queryClient.invalidateQueries({ queryKey: ['inpatient', 'worklist'] });
      requestId.current = crypto.randomUUID();
      setTexts({});
      setGlucoseContext(null);
      setReasons({});
      setServerAlerts([]);
      setTried(false);
      toasts.show({ title: 'Vitals saved' });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'alert_acknowledgement_required') {
        const ids = error.context?.['ruleIds'];
        if (Array.isArray(ids)) setServerAlerts(ids as ClinicalAlertRule[]);
      }
      requestId.current = crypto.randomUUID();
    },
  });

  const submit = () => {
    setTried(true);
    if (
      !online ||
      inputs.length === 0 ||
      bpError !== null ||
      fieldError ||
      alerts.some((a) => reasonOf(a.ruleId).length < REASON_MIN_LENGTH) ||
      save.isPending
    ) {
      return;
    }
    save.mutate();
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Save vitals' });

  const field = (key: VitalKey, extra: string | null = null) => {
    const definition = VITAL_SIGNS[key];
    const judged = judgeField(
      key,
      texts[key] ?? '',
      rules,
      ageDays,
      key === 'blood_glucose' ? glucoseContext : null,
    );
    const error = judged.error ?? extra ?? undefined;
    return (
      <VitalField
        key={key}
        label={definition.label}
        unit={definition.unitDisplay}
        value={texts[key] ?? ''}
        onChange={(value) => {
          setTexts((t) => ({ ...t, [key]: value }));
        }}
        provisional={judged.provisional}
        {...(judged.flag === undefined ? {} : { flag: judged.flag })}
        {...(error === undefined ? {} : { errorMessage: error })}
      />
    );
  };

  const saveError =
    save.error === null ||
    (save.error instanceof ApiError && save.error.code === 'alert_acknowledgement_required')
      ? null
      : save.error instanceof ApiError
        ? save.error.message
        : 'The vitals could not be saved. Try again.';

  return (
    <form
      className={styles.form}
      aria-label="Record vitals"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {rulesView.isError ? (
        <Notice tone="info">
          The clinical rules could not be loaded, so values are checked when you save.
        </Notice>
      ) : null}
      <div className={styles.vitalsGrid}>
        {ROUND_VITALS.map((key) => field(key, key === 'bp_systolic' ? bpError : null))}
      </div>
      {(texts.blood_glucose ?? '').trim() === '' ? null : (
        <RadioGroup
          label="Glucose taken"
          orientation="horizontal"
          value={glucoseContext}
          onChange={setGlucoseContext}
          options={[
            { id: 'random', label: 'Random' },
            { id: 'fasting', label: 'Fasting' },
          ]}
        />
      )}
      {alerts.map((alert) => (
        <AlertPanel
          key={alert.ruleId}
          title={alert.title}
          danger={alert.danger}
          reason={reasons[alert.ruleId] ?? ''}
          onReasonChange={(value) => {
            setReasons((r) => ({ ...r, [alert.ruleId]: value }));
          }}
          {...(tried && reasonOf(alert.ruleId).length < REASON_MIN_LENGTH
            ? { reasonError: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.` }
            : {})}
        >
          {alert.body}
        </AlertPanel>
      ))}
      {tried && inputs.length === 0 ? (
        <Notice tone="info">Enter at least one measurement to save.</Notice>
      ) : null}
      {saveError === null ? null : <Notice tone="error">{saveError}</Notice>}
      {online ? null : (
        <Notice tone="info">
          No connection to Wodi. Vitals are saved when the connection is back.
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save vitals
        </Button>
        <Kbd combo="mod+enter" />
        <span className={styles.meta}>Leave a field empty if it was not taken.</span>
      </div>
    </form>
  );
}
